/**
 * Aggregator-competitive intel hooks — brands, outlets, and rating snapshots
 * for tracked competitor restaurants on Google Places / Swiggy / Zomato.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { marketing } from '@/lib/supabase/marketing';

export interface CompetitorBrand {
  slug: string;
  display_name: string;
  ig_username: string | null;
  active: boolean;
  notes: string | null;
}

export interface CompetitorOutlet {
  id: string;
  brand_slug: string;
  city: string;
  locality: string;
  display_name: string | null;
  swiggy_url: string | null;
  zomato_url: string | null;
  google_place_id: string | null;
  active: boolean;
  added_at: string;
}

export interface RatingSnapshot {
  outlet_id: string;
  source: 'google' | 'swiggy' | 'zomato';
  snapshot_date: string;
  rating: number | null;
  review_count: number | null;
  delivery_eta_min: number | null;
  cost_for_two: number | null;
  photos?: string[] | null;
  badges?: string[] | null;
  must_try?: string[] | null;
  menu_offers?: Array<string | { label?: string; description?: string }> | null;
  raw?: {
    name?: string;
    address?: {
      streetAddress?: string;
      addressLocality?: string;
      addressRegion?: string;
      postalCode?: string;
    };
    servesCuisine?: string | string[];
    priceRange?: string;
  } | null;
}

export function useCompetitorBrands() {
  return useQuery({
    queryKey: ['aggr-comp-brands'],
    queryFn: async (): Promise<CompetitorBrand[]> => {
      const { data, error } = await marketing
        .from('aggregator_competitor_brand')
        .select('slug, display_name, ig_username, active, notes')
        .eq('active', true)
        .order('display_name');
      if (error) throw error;
      return (data ?? []) as CompetitorBrand[];
    },
    staleTime: 5 * 60_000,
  });
}

export function useCompetitorOutlets() {
  return useQuery({
    queryKey: ['aggr-comp-outlets'],
    queryFn: async (): Promise<CompetitorOutlet[]> => {
      const { data, error } = await marketing
        .from('aggregator_competitor_outlet')
        .select('*')
        .eq('active', true)
        .order('city')
        .order('locality');
      if (error) throw error;
      return (data ?? []) as CompetitorOutlet[];
    },
    staleTime: 60_000,
  });
}

export function useLatestRatings(daysWindow = 30) {
  return useQuery({
    queryKey: ['aggr-comp-ratings', daysWindow],
    queryFn: async (): Promise<RatingSnapshot[]> => {
      const since = new Date(Date.now() - daysWindow * 86400000)
        .toISOString()
        .slice(0, 10);
      const { data, error } = await marketing
        .from('aggregator_competitor_rating_snapshot')
        .select(
          'outlet_id, source, snapshot_date, rating, review_count, delivery_eta_min, cost_for_two, photos, badges, must_try, menu_offers',
        )
        .gte('snapshot_date', since)
        .order('snapshot_date', { ascending: false })
        .limit(5000);
      if (error) throw error;
      return (data ?? []) as RatingSnapshot[];
    },
    staleTime: 60_000,
  });
}

export function useAddOutlet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Omit<CompetitorOutlet, 'id' | 'added_at' | 'active'>) => {
      const { data, error } = await marketing
        .from('aggregator_competitor_outlet')
        .insert([payload])
        .select()
        .single();
      if (error) throw error;
      return data as CompetitorOutlet;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['aggr-comp-outlets'] }),
  });
}

export function useUpdateOutlet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<CompetitorOutlet> }) => {
      const { data, error } = await marketing
        .from('aggregator_competitor_outlet')
        .update(patch)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data as CompetitorOutlet;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['aggr-comp-outlets'] }),
  });
}

/**
 * Extract a Google `place_id` from a raw string. Only handles cases we
 * can solve on the client — for `maps.app.goo.gl/xxx` short URLs, call
 * `resolvePlaceIdServer()` which follows redirects.
 */
export function extractPlaceId(input: string): string | null {
  const t = input.trim();
  if (!t) return null;
  if (/^(ChIJ|GhIJ|EiC|Eig|ELg|EhY)[A-Za-z0-9_-]+$/.test(t)) return t;
  const mChIJ = t.match(/(ChIJ|GhIJ)[A-Za-z0-9_-]{10,}/);
  if (mChIJ?.[0]) return mChIJ[0];
  const m1 = t.match(/[!&]1s(0x[0-9a-f]+:0x[0-9a-f]+)/i);
  if (m1?.[1]) return m1[1];
  const m2 = t.match(/place_id[=:]([A-Za-z0-9_-]+)/i);
  if (m2?.[1]) return m2[1];
  return null;
}

/**
 * Server-side resolver for short URLs (maps.app.goo.gl/xxx) — follows
 * redirects on Cloudflare Pages and extracts an FTID or ChIJ.
 */
export async function resolvePlaceIdServer(
  input: string,
): Promise<{ place_id: string; source: string } | { error: string }> {
  const res = await fetch(`/api/resolve-place-id?url=${encodeURIComponent(input)}`);
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    return { error: (body as { error?: string })?.error ?? `HTTP ${res.status}` };
  }
  return (await res.json()) as { place_id: string; source: string };
}

/** ChIJ… IDs are Google Places-API-New compatible; FTIDs (0xhex:0xhex) aren't. */
export function isPlacesApiCompatible(placeId: string | null): boolean {
  if (!placeId) return false;
  return /^(ChIJ|GhIJ)[A-Za-z0-9_-]+$/.test(placeId);
}
